/**
 * Patches `console.error` and `console.warn` so every call also reports to system_error_logs.
 *
 * Why: hundreds of existing `console.error('[X] failed:', err)` calls in the codebase only
 * surface in the user's local DevTools. For beta testers, we never see them. Patching here
 * routes them through the same pipeline as uncaught errors.
 *
 * Behavior:
 * - Original console.error/warn behavior is preserved (still logs to DevTools).
 * - Stashes the originals at `console.__originalError` / `console.__originalWarn` so the
 *   logger can use them without re-triggering the patch (avoiding infinite recursion).
 * - Also adds an 'info'-level breadcrumb for plain `console.log` and `console.info`
 *   so we have execution trail. `console.info` es la vía que usa el código
 *   operativo que NO quiere convertirse en fila del panel (lazyWithRetry avisa
 *   por ahí de sus reintentos): migaja sí, informe no.
 */
import { reportFailure, reportWarning } from './logger';
import { addBreadcrumb } from './breadcrumbs';

let installed = false;

const formatArgs = (args: unknown[]): { message: string; errorArg: unknown } => {
    // If any arg is an Error, prefer it for the stack trace
    const errorArg = args.find(a => a instanceof Error);
    const parts = args.map(a => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a); } catch { return String(a); }
    });
    return { message: parts.join(' ').slice(0, 1000), errorArg };
};

const SUPPRESS_PREFIX_PATTERNS = [
    /^\[logger\]/,                  // our own logger output
    /^\[STUB\]/,                    // intentional stubs
    /^\[Software Failure Reported\]/, // legacy logger.ts message
    /^\[HMR\]/,                     // Vite hot reload
    /^Download the React DevTools/, // React's nag in dev
    /^\[Vue warn\]/,                // not us, but be safe
];

// Anywhere-in-message patterns for known third-party noise we can't change at
// the source. Keep this list narrow — only add things that are guaranteed
// non-actionable (user device state, deprecation notices we've already
// triaged, etc.). Anything actionable should stay visible.
const SUPPRESS_ANYWHERE_PATTERNS = [
    // Supabase gotrue-js fires this when the user's device clock is skewed
    // vs. server time. Pure client-side issue, nothing the app can fix.
    /Session as retrieved from URL was issued in the future/i,
    // Google Maps deprecation notices — already triaged, defer-listed.
    // \w+ covers every class Google deprecates with the same template:
    // Autocomplete, AutocompleteService, PlacesService, etc.
    /google\.maps\.places\.\w+ is not available to new customers/i,
    // Recharts logs this when ResponsiveContainer's first ResizeObserver
    // measurement on the parent yields -1 (parent transiently 0px during
    // initial mount or in a hidden tab). Even with minWidth/minHeight props
    // set, the warning fires *before* the fallback kicks in — Recharts
    // handles the measurement gracefully, the warning is just noise.
    /The width\(-1\) and height\(-1\) of chart should be greater than 0/i,
    // @googlemaps/js-api-loader console.error's each internal retry (it retries
    // 3x by design). Kept narrow — the TERMINAL failure is still reported once
    // via GoogleMapsContext's loadError effect, so a real outage stays visible.
    /Failed to load Google Maps script, retrying/i,
    // pdfjs falls back to a main-thread "fake worker" when the worker script
    // isn't wired up (contracts PDF preview). Benign — rendering still works,
    // just without a worker thread. (fp deba9917)
    /Warning: Setting up fake worker/i,
];

const shouldSuppress = (message: string): boolean => {
    const trimmed = message.trim();
    if (SUPPRESS_PREFIX_PATTERNS.some(p => p.test(trimmed))) return true;
    if (SUPPRESS_ANYWHERE_PATTERNS.some(p => p.test(trimmed))) return true;
    return false;
};

export const installConsolePatch = (): void => {
    if (installed || typeof console === 'undefined') return;
    installed = true;

    const origError = console.error.bind(console);
    const origWarn  = console.warn.bind(console);
    const origLog   = console.log.bind(console);
    const origInfo  = console.info.bind(console);

    // Stash originals so logger.ts can call them without re-triggering the patch
    (console as any).__originalError = origError;
    (console as any).__originalWarn  = origWarn;

    console.error = (...args: unknown[]) => {
        origError(...args); // preserve DevTools behavior first
        try {
            const { message, errorArg } = formatArgs(args);
            if (!shouldSuppress(message)) {
                // If an Error was among the args, use it (its stack is more useful);
                // otherwise wrap the joined message so we still get a stack from here.
                //
                // `context` carries the FULL joined text, including the call-site
                // prefix. Without it, `console.error('[AI Chat] failed:', err)` and
                // `console.error('[parseMaterialFile] failed:', err)` both report as
                // bare `err.message` — indistinguishable in the admin panel when the
                // upstream text is generic ("Edge Function returned a non-2xx status
                // code"), which is exactly the common case.
                //
                // Deliberately NOT folded into `component`: fingerprintFor derives
                // from (message, stack, component), so changing either would re-key
                // every console.error report in the app and resurrect every
                // already-resolved row as new. Splitting those fingerprints is a
                // real improvement but it costs a one-time wave of false "new"
                // errors on deploy, so it needs an explicit decision, not a
                // side effect of this fix.
                //
                // Esa decisión ya se tomó, en el otro sentido: logger.ts saca el
                // primer frame del fingerprint precisamente para 'console.error'
                // y 'console.warn', porque ahí el frame es siempre el nombre
                // minificado del propio logger. El re-fingerprinting de una vez
                // ya está pagado; este `context` sigue siendo lo que distingue
                // una llamada de otra a ojo en el panel.
                reportFailure(errorArg ?? new Error(message), {
                    component: 'console.error',
                    severity: 'error',
                    context: message,
                });
            }
        } catch { /* never throw from a patched console method */ }
    };

    console.warn = (...args: unknown[]) => {
        origWarn(...args);
        try {
            const { message } = formatArgs(args);
            if (!shouldSuppress(message)) {
                reportWarning(message, { component: 'console.warn' });
            }
        } catch { /* swallow */ }
    };

    // Capture console.log as info-level breadcrumbs only (don't insert into DB — too noisy)
    console.log = (...args: unknown[]) => {
        origLog(...args);
        try {
            const { message } = formatArgs(args);
            if (message && !shouldSuppress(message)) {
                addBreadcrumb({ cat: 'console', msg: message.slice(0, 200) });
            }
        } catch { /* swallow */ }
    };

    // Igual que console.log. Faltaba, y no era inocuo: lazyWithRetry usa
    // console.info a propósito para contar sus reintentos sin ensuciar el panel,
    // y esa traza no llegaba a las migajas — de modo que cuando el fallo SÍ se
    // reportaba, el informe no llevaba ni rastro de los dos intentos previos.
    console.info = (...args: unknown[]) => {
        origInfo(...args);
        try {
            const { message } = formatArgs(args);
            if (message && !shouldSuppress(message)) {
                addBreadcrumb({ cat: 'console', msg: message.slice(0, 200) });
            }
        } catch { /* swallow */ }
    };
};
